import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CallOutlined,
  ContentCutOutlined,
  EventOutlined,
  PetsOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { APP_CONFIG } from "../../config/appConfig";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { cashService, groomingService } from "../../services";
import GroomingDiagnosticDialog from "./components/GroomingDiagnosticDialog";

const ACTIVE_APPOINTMENT_STATUSES = "SCHEDULED,CONFIRMED,CHECKED_IN,CONVERTED";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("es-GT", {
    timeZone: APP_CONFIG.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

const getAppointmentStatusMeta = (appointment) => {
  const normalized = String(appointment?.status || appointment || "").toUpperCase();
  if (normalized === "CONFIRMED") return { label: "Confirmada", color: "info" };
  if (normalized === "CHECKED_IN") return { label: "Recepcionada", color: "warning" };
  if (normalized === "CONVERTED") {
    const order = appointment?.order || appointment?.order_detail || null;
    const orderUiStatus = String(order?.ui_status || "").toUpperCase();
    const paymentStatus = String(order?.payment_status || "").toUpperCase();
    const saleStatus = String(order?.sale?.status || "").toUpperCase();
    if (
      orderUiStatus === "COBRADO" ||
      paymentStatus === "PAID" ||
      ["COMPLETED", "RETURNED"].includes(saleStatus)
    ) {
      return { label: "Finalizado", color: "success" };
    }
    if (
      orderUiStatus === "CANCELADO" ||
      paymentStatus === "VOID" ||
      ["VOIDED", "CANCELED"].includes(saleStatus)
    ) {
      return { label: "Cancelada", color: "error" };
    }
    return { label: "En proceso", color: "info" };
  }
  if (normalized === "COMPLETED") return { label: "Completada", color: "success" };
  if (normalized === "CANCELED") return { label: "Cancelada", color: "error" };
  if (normalized === "NO_SHOW") return { label: "No llego", color: "default" };
  return { label: "Programada", color: "default" };
};

const isSalePaid = (sale) =>
  ["COMPLETED", "RETURNED"].includes(String(sale?.status || "").toUpperCase());

const isSaleVoidedOrCanceled = (sale) =>
  ["VOIDED", "CANCELED"].includes(String(sale?.status || "").toUpperCase());

const resolveOrderUiStatus = (order) => {
  if (!order) return "EN_PROCESO";
  const explicitUi = String(order?.ui_status || "").toUpperCase();
  if (["EN_PROCESO", "LISTO_ENTREGA", "COBRADO", "CANCELADO"].includes(explicitUi)) {
    return explicitUi;
  }
  if (isSaleVoidedOrCanceled(order?.sale)) return "CANCELADO";
  if (String(order?.status || "").toUpperCase() === "CANCELED" || order?.canceled_at) return "CANCELADO";
  if (Boolean(order?.is_paid) || isSalePaid(order?.sale)) return "COBRADO";
  if (["READY", "DELIVERED"].includes(String(order?.status || "").toUpperCase())) return "LISTO_ENTREGA";
  return "EN_PROCESO";
};

const getOrderStatusMeta = (order) => {
  const normalized = resolveOrderUiStatus(order);
  if (normalized === "EN_PROCESO") return { label: "En proceso", color: "warning" };
  if (normalized === "LISTO_ENTREGA") return { label: "Listo para cobrar", color: "info" };
  if (normalized === "COBRADO") return { label: "Cobrado", color: "success" };
  if (normalized === "CANCELADO") return { label: "Cancelado", color: "error" };
  return { label: "En proceso", color: "default" };
};

const isAppointmentInvalidForOrderConversion = (appointment) => {
  if (!appointment) return true;
  const status = String(appointment?.status || "").toUpperCase();
  const timeState = String(appointment?.time_state || "").toUpperCase();
  if (["CANCELED", "NO_SHOW", "COMPLETED"].includes(status)) return true;
  if (timeState === "MISSED") return true;
  return false;
};

const getTodayQueryValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function AppointmentQuickCard({
  appointment,
  selected = false,
  onSelect,
  onReceive,
  converting = false,
  disabledReceive = false,
}) {
  const statusMeta = getAppointmentStatusMeta(appointment);
  const conversionBlocked = isAppointmentInvalidForOrderConversion(appointment);

  return (
    <Paper
      variant="outlined"
      onClick={onSelect}
      sx={(theme) => ({
        p: 1.5,
        cursor: "pointer",
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        boxShadow: selected ? `0 0 0 1px ${theme.palette.primary.main}` : "none",
        transition: "border-color 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      })}
    >
      <Stack spacing={1.2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {appointment?.pet?.name || "Mascota"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {appointment?.customer?.name || "Cliente"}
            </Typography>
          </Box>
          <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <EventOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(appointment?.scheduled_start)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="text" onClick={onSelect}>
            Ver cita
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onReceive}
            disabled={conversionBlocked || converting || disabledReceive}
          >
            {converting ? "Procesando..." : "Recibir perro"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function GroomingReceptionPage() {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;

  const canView = hasPermission(effectivePerms, "grooming_view", isSuperuser);
  const canManage = hasPermission(effectivePerms, "grooming_manage", isSuperuser);

  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [cashStatusKnown, setCashStatusKnown] = useState(false);
  const [hasOpenCashSession, setHasOpenCashSession] = useState(false);
  const [activeOrders, setActiveOrders] = useState([]);
  const [loadingActiveOrders, setLoadingActiveOrders] = useState(false);
  const [diagnosticDialog, setDiagnosticDialog] = useState({
    open: false,
    orderId: null,
    initialOrder: null,
  });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadCashStatus = useCallback(async () => {
    if (!branchId) {
      setCashStatusKnown(false);
      setHasOpenCashSession(false);
      return;
    }
    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      const hasOpenSession = Boolean(data?.session && data.session.status === "OPEN");
      setCashStatusKnown(true);
      setHasOpenCashSession(hasOpenSession);
    } catch {
      setCashStatusKnown(false);
      setHasOpenCashSession(false);
    }
  }, [branchId]);

  const loadAppointments = useCallback(async () => {
    if (!branchId || !canView) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const today = getTodayQueryValue();
      const data = await groomingService.listAppointments({
        branch_id: branchId,
        q: String(search || "").trim() || undefined,
        statuses: ACTIVE_APPOINTMENT_STATUSES,
        date_field: "scheduled_start",
        date_from: today,
        date_to: today,
      });
      setRows(toArray(data));
    } catch (error) {
      setRows([]);
      showSnack(extractErrorMessage(error, "No se pudieron cargar las citas de hoy."), "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canView, search, showSnack]);

  const loadActiveOrders = useCallback(
    async ({ silent = false } = {}) => {
      if (!branchId || !canView) {
        setActiveOrders([]);
        setLoadingActiveOrders(false);
        return;
      }

      if (!silent) {
        setLoadingActiveOrders(true);
      }

      try {
        const data = await groomingService.listOrders({
          branch_id: branchId,
          is_closed: "false",
          page_size: 100,
        });
        const rows = toArray(data);
        const filtered = rows.filter((order) =>
          ["EN_PROCESO", "LISTO_ENTREGA"].includes(resolveOrderUiStatus(order))
        );
        setActiveOrders(filtered);
      } catch (error) {
        if (!silent) {
          showSnack(extractErrorMessage(error, "No se pudieron cargar las ordenes activas."), "error");
        }
      } finally {
        if (!silent) {
          setLoadingActiveOrders(false);
        }
      }
    },
    [branchId, canView, showSnack]
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadAppointments();
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [loadAppointments]);

  useEffect(() => {
    loadActiveOrders();
  }, [loadActiveOrders]);

  useEffect(() => {
    if (!branchId || !canView) return undefined;
    const refresh = () => {
      loadActiveOrders({ silent: true });
      loadAppointments();
    };
    const intervalId = window.setInterval(refresh, 25000);
    const onFocus = () => refresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [branchId, canView, loadActiveOrders, loadAppointments]);

  useEffect(() => {
    loadCashStatus();
  }, [loadCashStatus]);

  useEffect(() => {
    if (rows.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      const hasCurrent = rows.some((row) => Number(row.id) === Number(prev));
      return hasCurrent ? prev : rows[0].id;
    });
  }, [rows]);

  useEffect(() => {
    searchInputRef.current?.focus?.();
  }, []);

  const selectedAppointment = useMemo(
    () => rows.find((row) => Number(row.id) === Number(selectedId)) || null,
    [rows, selectedId]
  );

  const handleReceiveDog = useCallback(
    async (appointment) => {
      if (!branchId || !appointment?.id || !canManage) return;
      if (isAppointmentInvalidForOrderConversion(appointment)) {
        showSnack("La cita no se puede convertir porque esta cancelada, perdida o vencida.", "warning");
        return;
      }

      if (appointment?.order?.id) {
        navigate(`/grooming/orders/${appointment.order.id}`);
        return;
      }

      try {
        const data = await cashService.currentSession({ branch_id: branchId });
        const hasOpenSession = Boolean(data?.session && data.session.status === "OPEN");
        setCashStatusKnown(true);
        setHasOpenCashSession(hasOpenSession);
        if (!hasOpenSession) {
          showSnack(
            "Debes tener una sesion de caja abierta para recibir perros y crear ordenes grooming.",
            "warning"
          );
          return;
        }
      } catch {
        // Si no se puede resolver desde frontend, el backend valida igual.
      }

      setConvertingId(appointment.id);
      try {
        const data = await groomingService.convertAppointmentToOrder(appointment.id, {
          branch_id: branchId,
        });
        const orderId = data?.order?.id || data?.appointment?.order?.id;
        showSnack("Perro recibido y orden grooming creada correctamente.", "success");
        await Promise.all([loadAppointments(), loadActiveOrders({ silent: true })]);
        if (orderId) {
          setDiagnosticDialog({
            open: true,
            orderId,
            initialOrder: data?.order || null,
          });
          return;
        }
      } catch (error) {
        showSnack(extractErrorMessage(error, "No se pudo recibir la cita."), "error");
      } finally {
        setConvertingId(null);
      }
    },
    [branchId, canManage, loadActiveOrders, loadAppointments, navigate, showSnack]
  );

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      if (selectedAppointment) {
        handleReceiveDog(selectedAppointment);
      }
    },
    [handleReceiveDog, selectedAppointment]
  );

  if (!canView) {
    return <Alert severity="warning">No tienes permisos para operar Grooming.</Alert>;
  }

  if (!branchId) {
    return <Alert severity="info">Selecciona una sucursal para trabajar Grooming.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <MainText
          title="Recepcion grooming"
          subtitle="Busqueda rapida de citas del dia para recibir en mostrador"
          icon={ContentCutOutlined}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" onClick={() => navigate("/grooming/appointments")}>
            Ir a agenda
          </Button>
          <Button variant="outlined" onClick={() => navigate("/grooming/orders")}>
            Ver ordenes
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              inputRef={searchInputRef}
              autoFocus
              label="Buscar por telefono"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CallOutlined color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchOutlined color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Stack spacing={1.25}>
              {cashStatusKnown && !hasOpenCashSession ? (
                <Alert severity="warning">
                  No hay caja abierta en esta sucursal. Puedes consultar citas, pero no recibir perros.
                </Alert>
              ) : null}
              {loading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Skeleton variant="text" width="40%" height={26} />
                        <Skeleton variant="text" width="72%" height={22} />
                        <Skeleton variant="rounded" height={36} />
                      </Stack>
                    </Paper>
                  ))
                : rows.length === 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        textAlign: "center",
                        borderStyle: "dashed",
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        No hay citas activas para hoy
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Prueba con otro telefono o revisa la agenda del dia.
                      </Typography>
                    </Paper>
                  ) : (
                    rows.map((appointment) => (
                      <AppointmentQuickCard
                        key={appointment.id}
                        appointment={appointment}
                        selected={Number(selectedId) === Number(appointment.id)}
                        converting={Number(convertingId) === Number(appointment.id)}
                        disabledReceive={
                          isAppointmentInvalidForOrderConversion(appointment) ||
                          (!appointment?.order?.id && cashStatusKnown && !hasOpenCashSession)
                        }
                        onSelect={() => setSelectedId(appointment.id)}
                        onReceive={() => handleReceiveDog(appointment)}
                      />
                    ))
                  )}
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, minHeight: 420 }}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PetsOutlined color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Detalle rapido
              </Typography>
            </Stack>

            {!selectedAppointment ? (
              <Box
                sx={(theme) => ({
                  flex: 1,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  display: "grid",
                  placeItems: "center",
                  px: 2,
                  textAlign: "center",
                })}
              >
                <Stack spacing={1} alignItems="center">
                  <ContentCutOutlined color="disabled" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Selecciona una cita
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Al buscar por telefono se seleccionara automaticamente la primera coincidencia util.
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.1}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {selectedAppointment.pet?.name || "Mascota"}
                    </Typography>
                    <Chip
                      size="small"
                      label={getAppointmentStatusMeta(selectedAppointment).label}
                      color={getAppointmentStatusMeta(selectedAppointment).color}
                      sx={{ alignSelf: "flex-start" }}
                    />
                    <Typography variant="body2">
                      <strong>Cliente:</strong> {selectedAppointment.customer?.name || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Telefono:</strong> {selectedAppointment.customer?.phone_number || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Hora:</strong> {formatDateTime(selectedAppointment.scheduled_start)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Servicio solicitado:</strong> {selectedAppointment.requested_service_name || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Groomer preferido:</strong> {selectedAppointment.assigned_to?.name || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Notas:</strong> {selectedAppointment.requested_notes || "-"}
                    </Typography>
                  </Stack>
                </Paper>

                <Box sx={{ mt: "auto" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => handleReceiveDog(selectedAppointment)}
                    disabled={
                      !canManage ||
                      isAppointmentInvalidForOrderConversion(selectedAppointment) ||
                      Boolean(convertingId) ||
                      (!selectedAppointment?.order?.id && cashStatusKnown && !hasOpenCashSession)
                    }
                  >
                    {Number(convertingId) === Number(selectedAppointment.id) ? "Procesando..." : "Recibir perro"}
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Ordenes activas en tiempo real
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={loadingActiveOrders ? "Actualizando..." : `${activeOrders.length} activa(s)`}
            />
          </Stack>

          {loadingActiveOrders && activeOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Cargando ordenes activas...
            </Typography>
          ) : activeOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay ordenes en proceso o listas para cobrar en este momento.
            </Typography>
          ) : (
            <Stack spacing={0.8}>
              {activeOrders.map((activeOrder) => {
                const statusMeta = getOrderStatusMeta(activeOrder);
                const uiStatus = resolveOrderUiStatus(activeOrder);
                const actionLabel = uiStatus === "LISTO_ENTREGA" ? "Cobrar" : "Continuar";
                return (
                  <Paper key={activeOrder.id} variant="outlined" sx={{ p: 1 }}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {activeOrder?.pet?.name || "Mascota"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {activeOrder?.customer?.name || "Cliente"}
                        </Typography>
                        <Chip size="small" variant="outlined" color={statusMeta.color} label={statusMeta.label} />
                        <Chip size="small" variant="outlined" label={`Total: Q${Number(activeOrder?.total || 0).toFixed(2)}`} />
                      </Stack>
                      <Button
                        size="small"
                        variant={uiStatus === "LISTO_ENTREGA" ? "contained" : "outlined"}
                        onClick={() => navigate(`/grooming/orders/${activeOrder.id}`)}
                      >
                        {actionLabel}
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Paper>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />

      <GroomingDiagnosticDialog
        open={diagnosticDialog.open}
        branchId={branchId}
        orderId={diagnosticDialog.orderId}
        initialOrder={diagnosticDialog.initialOrder}
        canManage={canManage}
        onClose={() =>
          setDiagnosticDialog({
            open: false,
            orderId: null,
            initialOrder: null,
          })
        }
        onContinue={(orderData) => {
          const nextOrderId = orderData?.id || diagnosticDialog.orderId;
          setDiagnosticDialog({
            open: false,
            orderId: null,
            initialOrder: null,
          });
          if (nextOrderId) {
            navigate(`/grooming/orders/${nextOrderId}`);
          }
        }}
      />
    </Box>
  );
}

export default GroomingReceptionPage;
